import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import RightArrow from "../../../../assets/Icons/Analytics/blackRightArrow.svg";
import { AnalyticsService } from "../../../../services/analyticsService";
import { useSpinner } from "../../../../components/spinner/SpinnerContext";
import DefaultEventBanner from "../../../../assets/theme/images/default-event-banner.svg";
import { DynamicTable } from "../../../../components/DynamicTable";
import { Search, X } from "lucide-react";
import { STATUS_CODE_MAP } from "../../../../utils/constants";

interface ConnectionRequestRow {
  senderName: string;
  receiverName: string;
  senderEmail: string;
  receiverEmail: string;
  senderResponseStatus: string;
  receiverResponseStatus: string;
}

// Sample data for testing
const sampleConnectionRequests: ConnectionRequestRow[] = [
  {
    senderName: "Aarav Mehta",
    senderEmail: "aarav.mehta@gmail.com",
    receiverName: "Neha Sharma",
    receiverEmail: "neha.sharma@yopmail.com",
    senderResponseStatus: "ACCEPTED",
    receiverResponseStatus: "PENDING",
  },
  {
    senderName: "Rohit Verma",
    senderEmail: "rohit.verma@gmail.com",
    receiverName: "Pooja Singh",
    receiverEmail: "pooja.singh@yopmail.com",
    senderResponseStatus: "PENDING",
    receiverResponseStatus: "PENDING",
  },
  {
    senderName: "Ananya Iyer",
    senderEmail: "ananya.iyer@gmail.com",
    receiverName: "Karan Malhotra",
    receiverEmail: "karan.malhotra@yopmail.com",
    senderResponseStatus: "ACCEPTED",
    receiverResponseStatus: "ACCEPTED",
  },
  {
    senderName: "Vikram Patel",
    senderEmail: "vikram.patel@gmail.com",
    receiverName: "Riya Kapoor",
    receiverEmail: "riya.kapoor@yopmail.com",
    senderResponseStatus: "PENDING",
    receiverResponseStatus: "ACCEPTED",
  },
  {
    senderName: "Siddharth Jain",
    senderEmail: "siddharth.jain@gmail.com",
    receiverName: "Meera Nair",
    receiverEmail: "meera.nair@yopmail.com",
    senderResponseStatus: "ACCEPTED",
    receiverResponseStatus: "PENDING",
  },
  {
    senderName: "Nikhil Agarwal",
    senderEmail: "nikhil.agarwal@gmail.com",
    receiverName: "Sneha Roy",
    receiverEmail: "sneha.roy@yopmail.com",
    senderResponseStatus: "PENDING",
    receiverResponseStatus: "PENDING",
  },
  {
    senderName: "Priya Khanna",
    senderEmail: "priya.khanna@gmail.com",
    receiverName: "Arjun Rao",
    receiverEmail: "arjun.rao@yopmail.com",
    senderResponseStatus: "ACCEPTED",
    receiverResponseStatus: "ACCEPTED",
  },
  {
    senderName: "Rahul Sethi",
    senderEmail: "rahul.sethi@gmail.com",
    receiverName: "Ishita Gupta",
    receiverEmail: "ishita.gupta@yopmail.com",
    senderResponseStatus: "PENDING",
    receiverResponseStatus: "ACCEPTED",
  },
  {
    senderName: "Kunal Bansal",
    senderEmail: "kunal.bansal@gmail.com",
    receiverName: "Aditi Joshi",
    receiverEmail: "aditi.joshi@yopmail.com",
    senderResponseStatus: "ACCEPTED",
    receiverResponseStatus: "PENDING",
  },
  {
    senderName: "Manish Kulkarni",
    senderEmail: "manish.kulkarni@gmail.com",
    receiverName: "Tanvi Deshpande",
    receiverEmail: "tanvi.deshpande@yopmail.com",
    senderResponseStatus: "PENDING",
    receiverResponseStatus: "PENDING",
  },
];

export default function MatchedConnectionRequestsList() {
  const navigate = useNavigate();
  const location = useLocation();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const PAGE_SIZE = 5; // Mobile-friendly

  const queryParams = new URLSearchParams(location.search);
  const eventId: number | null = queryParams.get("eventid")
    ? Number(queryParams.get("eventid"))
    : null;
  const hostId: number | null = queryParams.get("hostid")
    ? Number(queryParams.get("hostid"))
    : null;
  const { eventName, bannerImage } = location.state || {};

  const { setShowSpinner } = useSpinner();
  const [connectionRequests, setConnectionRequests] = useState<
    ConnectionRequestRow[]
  >([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (eventId && hostId) {
      getEventMatchedGuestConnectionRequests(eventId, hostId);
    }
  }, [eventId, hostId]);

  const getEventMatchedGuestConnectionRequests = async (
    eventId: number,
    hostId: number,
    search?: string
  ) => {
    try {
      setShowSpinner(true);
      setLoading(true);
      const response =
        await AnalyticsService.getEventMatchedGuestConnectionRequests({
          eventId,
          hostId,
          search,
          page,
        });
      // Uncomment when API works
      setConnectionRequests(
        response?.data?.data && response?.data?.data.length > 0
          ? response?.data?.data
          : []
      );
      setTotalCount(
        response?.data?.data && response?.data?.data.length > 0
          ? response?.data?.totalCount
          : 0
      );
      setTotalPages(response?.data?.totalPages || totalPages1);
      setShowSpinner(false);
      setLoading(false);
    } catch (error) {
      setShowSpinner(false);
      setLoading(false);
      console.error("Error fetching connection requests", error);
    }
  };

  const onSearch = (query: string) => {
    // setSearch(query);
    // setPage(1)
    if (query && query?.length > 0) {
      getEventMatchedGuestConnectionRequests(eventId!, hostId!, query);
    } else {
      getEventMatchedGuestConnectionRequests(eventId || 0, hostId || 0);
    }
  };

  // Columns for table
  const columns = [
    { label: "Sender Name", key: "senderName" },
    { label: "Sender Email", key: "senderEmail" },
    {
      label: "Sender Status",
      key: "senderResponseStatus",
      render: (value: string) => <StatusPill value={value} />,
    },
    { label: "Receiver Name", key: "receiverName" },
    { label: "Receiver Email", key: "receiverEmail" },
    {
      label: "Receiver Status",
      key: "receiverResponseStatus",
      render: (value: string) => <StatusPill value={value} />,
    },
  ];

  const tableData = useMemo(() => connectionRequests, [connectionRequests]);

  // Filter & search
  const filteredData = useMemo(() => {
    if (!search) return tableData;
    const q = search.toLowerCase();
    return tableData.filter((item) =>
      [
        item.senderName,
        item.senderEmail,
        item.receiverName,
        item.receiverEmail,
      ].some((val) => val.toLowerCase().includes(q))
    );
  }, [search, tableData]);

  // Pagination
  const totalPages1 = Math.ceil(filteredData.length / PAGE_SIZE);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredData.slice(start, start + PAGE_SIZE);
  }, [filteredData, page]);

  //   useEffect(() => setPage(1), [search]);


  // Status pill component
  const StatusPill = ({ value }: { value: string }) => {
    const base = "px-2 py-0.5 rounded-full text-xs font-semibold";
    const styles =
      value && value?.length > 1
        ? value === "Handshake"
          ? "bg-green-100 text-green-700"
          : value === "Heart"
          ? "bg-red-100 text-red-700"
          : "bg-yellow-100 text-yellow-600"
        : "";
    return <span className={`${base} ${styles}`}>{STATUS_CODE_MAP[value]}</span>;
  };
  // Mobile card view
  const MobileConnectionCard = ({ item }: { item: ConnectionRequestRow }) => (
    <div className="bg-[#FAF2F6] rounded-xl p-4 shadow-sm space-y-3">
      <div>
        <p className="text-xs text-[#786469] font-semibold">Sender</p>
        <p className="font-medium text-sm">{item.senderName}</p>
        <p className="text-xs text-gray-600">{item.senderEmail}</p>
        <div className="mt-1">
          <StatusPill value={item.senderResponseStatus} />
        </div>
      </div>
      <div className="h-px bg-gray-200" />
      <div>
        <p className="text-xs text-[#786469] font-semibold">Receiver</p>
        <p className="font-medium text-sm">{item.receiverName}</p>
        <p className="text-xs text-gray-600">{item.receiverEmail}</p>
        <div className="mt-1">
          <StatusPill value={item.receiverResponseStatus} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full py-6 px-4 lg:px-8 md:px-8 space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between gap-3">
        <div className="flex items-center gap-4 font-bold text-base">
          <img
            src={RightArrow}
            alt="Back"
            className="w-5 h-5 cursor-pointer rotate-180"
            onClick={() => navigate(-1)}
          />
          <span className="text-[#786469] font-bold text-[17px] md:text-2xl">
            Analytics
          </span>
          <img src={RightArrow} alt="Arrow" className="w-5 h-5" />
          <span className="font-bold text-sm md:text-2xl">Matches</span>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-[#FAF2F6] rounded-2xl w-full md:w-[40%]">
          <img
            src={bannerImage || DefaultEventBanner}
            alt="Event"
            className="w-[31px] h-[31px] rounded-md"
          />
          <span className="truncate text-[14px]">{eventName}</span>
        </div>
      </div>

      {/* Search box */}

      {/* Table for desktop */}
      <div className="hidden md:block mt-4">
          <DynamicTable
            columns={columns}
            data={connectionRequests}
            totalCount={totalCount}
            pageSize={PAGE_SIZE}
            loading={loading}
            onSearch={(val: string) => onSearch(val)}
          />
      </div>

      {/* Mobile cards */}
      <div className="md:hidden mt-4 space-y-4">
        {/* {paginatedData.length > 0 ? ( */}
          <>
            <div className="w-full  mt-4 flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search sender or receiver..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-3 pr-9 py-2 border border-gray-300 rounded-lg text-sm
                 focus:outline-none focus:ring focus:ring-indigo-200"
                />

                {/* Clear Icon (inside input) */}
                {search && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearch("");
                      onSearch("");
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 
                   text-red-400 hover:text-red-700"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              <button
                onClick={() => onSearch(search)}
                disabled={!search.trim()}
                className="px-3 py-2 rounded-md bg-indigo-600 text-white font-semibold
               disabled:bg-gray-300 disabled:cursor-not-allowed
               hover:enabled:bg-indigo-700 transition-colors duration-200"
              >
                <Search size={16} />
              </button>
            </div>
            {connectionRequests && connectionRequests?.length > 0 ? (connectionRequests.map((item, index) => (
              <MobileConnectionCard key={index} item={item} />
            ))) : (
              <div className="py-10 text-center text-gray-500">
                No matches found.
              </div>
            )}
          </>
      </div>

      {/* Mobile pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-6 md:hidden">
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1 text-sm rounded bg-gray-200 disabled:opacity-50"
          >
            Prev
          </button>
          <span className="text-xs text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1 text-sm rounded bg-gray-200 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
